import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyCounsellorProjects } from '../../services/counsellorService';
import { generateReport } from '../../services/reportService';
import { useToast } from '../../context/ToastContext';
import styles from './CounsellorReports.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const CounsellorReports = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [reportType, setReportType] = useState('project');
  const [selectedProject, setSelectedProject] = useState('');
  const toast = useToast();
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await getMyCounsellorProjects();
      setProjects(data);
      if (data.length > 0) {
        setSelectedProject(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedProject && reportType === 'project') {
      toast.warning('Please select a project');
      return;
    }

    setGenerating(true);
    try {
      const reportData = {
        report_type: reportType,
        project_id: reportType === 'project' ? selectedProject : null
      };

      const response = await generateReport(reportData);
      
      // Create a blob from the response and trigger download
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading..." />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Reports</h1>
      </div>

      <Card>
        <div className={styles.reportForm}>
          <h2 className={styles.sectionTitle}>Generate Report</h2>
          
          <div className={styles.formGroup}>
            <label htmlFor="reportType">Report Type</label>
            <select
              id="reportType"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className={styles.select}
            >
              <option value="project">Project Report</option>
              <option value="progress">Progress Report</option>
              <option value="summary">Summary Report</option>
            </select>
          </div>

          {reportType === 'project' && (
            <div className={styles.formGroup}>
              <label htmlFor="project">Select Project</label>
              <select
                id="project"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className={styles.select}
              >
                {sortProjectsByName(projects).map(project => (
                  <option key={project.id} value={project.id}>
                    {project.project_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            onClick={handleGenerateReport}
            disabled={generating || (reportType === 'project' && !selectedProject)}
            className={styles.generateBtn}
          >
            {generating ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </Card>

      <Card>
        <div className={styles.reportInfo}>
          <h2 className={styles.sectionTitle}>Available Reports</h2>
          <div className={styles.reportList}>
            <div className={styles.reportItem}>
              <div className={styles.reportIcon}>RP</div>
              <div className={styles.reportDetails}>
                <h3>Project Report</h3>
                <p>Detailed report for a specific project including milestones, progress, and team members</p>
              </div>
            </div>
            <div className={styles.reportItem}>
              <div className={styles.reportIcon}>TR</div>
              <div className={styles.reportDetails}>
                <h3>Progress Report</h3>
                <p>Overall progress across all assigned projects</p>
              </div>
            </div>
            <div className={styles.reportItem}>
              <div className={styles.reportIcon}>DOC</div>
              <div className={styles.reportDetails}>
                <h3>Summary Report</h3>
                <p>High-level summary of all projects and their current status</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CounsellorReports;

